import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { Modal, Button } from "@toddle-edu/ds-web";
import { ClockTimerOutlined, BulletDotFilled } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import {
  getFormattedDuration,
  getIncrementedDate,
  getTodaysDate,
  checkIsDateSameOrAfter,
  getDateTimeDifference,
} from "Utils/DateTimeHelpers";
import classes from "./WorksheetTimer.scss";
import {
  handleQuitSebNavigate,
  isStudentEndMode,
  isWorksheetWebView,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetMonitoringEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { updateEnableAutoSubmit } from "Worksheet/modules/WorksheetModules";
import moment from "moment";
import { Trans } from "react-i18next";
import { getIsStudentAssignmentRevampEnabled } from "ClassRoom/modules/utils";
import { getWorksheetTimerState } from "./WorksheetTimerUtils";
import { isMobile } from "Utils";

const components = { b: <b /> };

const SHOW_DIALOG_AT_MINS = 5;

/**
 * TEST ABOUT TO COMPLETE DIALOG
 */
const TestAboutToCompleteDialog = ({ worksheetTitle, onClose }) => {
  const { t } = useI18n();

  return (
    <Modal
      width={"440px"}
      height={"max-content"}
      onClose={onClose}
      testId="worksheet-worksheetTimer-closeModal"
    >
      <Modal.Header
        title={t("classRoom:test_about_to_complete_title", {
          time: SHOW_DIALOG_AT_MINS,
        })}
        isCloseable={true}
      />
      <Modal.Body>
        <div className={classNames("text-body-1", classes.dialogMessage)}>
          <Trans
            i18nKey="classRoom:test_about_to_complete_message"
            components={components}
            values={{ title: worksheetTitle, time: SHOW_DIALOG_AT_MINS }}
          >
            <b>{{ title: worksheetTitle }} - worksheet</b> ends in
            <b>{{ time: SHOW_DIALOG_AT_MINS }}</b> minutes. Worksheet will be
            automatically submitted once the time ends.
          </Trans>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerContainer}>
          <Button
            dsVersion="2.0"
            variant="progressive"
            size="large"
            _legacy_size={"large"}
            _legacy_variant={"progressive"}
            onClick={onClose}
            testId="worksheet-worksheetTimer-modalFooter-closeModal"
          >
            {t("common:okay")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * WORKSHEET TIMER
 * Add End state for Closes on if date is passed ref from desktop recorded video
 * Check all cases for Closes date and duration, < > = all
 */

const WorksheetTimerV2 = React.memo(props => {
  const {
    events,
    timedTestDuration,
    isWorksheetSubmissionModal = false,
    worksheetTitle,
    toggleWorksheetModal,
    onSubmitTimedWorksheet,
    setWorksheetWebViewRecentlySubmitted,
    showBackground,
    isSafeBrowserEnabled,
    isInSafeExamBrowser,
    isQuestionTheaterModeEnable,
    updateEnableAutoSubmit,
    closeSubmissionDate,
    isWorksheetPaused,
    isFixedStartTime,
    publishedAt,
    submissionEndTime,
    onTimesUp,
    isSubmitted,
  } = props;

  const { t } = useI18n();
  const intervalRef = useRef();
  const [, setTimer] = useState();
  const [
    showTestAboutToCompleteDialog,
    setShowTestAboutToCompleteDialog,
  ] = useState(false);
  const [showDestructiveTimer, setShowDestructiveTimer] = useState(false);

  const isStudentUIRevamp = isStudentEndMode();
  const isStudentAssignmentRevamp = getIsStudentAssignmentRevampEnabled();
  const isMobileDevice = isMobile();

  const {
    state = "NOT_STARTED",
    remainingDuration = 0,
    isTestStarted = false,
    isTestEnded = false,
    isReopened = false,
  } = getWorksheetTimerState({
    events,
    publishedAt,
    isFixedStartTime,
    timedTestDuration,
    closeSubmissionDate,
    submissionEndTime,
    isWorksheetPaused,
    isSubmitted,
  });

  // Interval to update timer every second
  useEffect(() => {
    const shouldSkipInterval = isTestEnded || isWorksheetPaused;
    if (shouldSkipInterval) {
      clearInterval(intervalRef?.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimer(remainingDuration);

      if (remainingDuration <= 0) {
        setShowDestructiveTimer(true);
        clearInterval(intervalRef.current);
      }
    }, 1000);

    return () => {
      clearInterval(intervalRef?.current);
    };
  }, [
    isTestStarted,
    isTestEnded,
    isWorksheetPaused,
    isReopened,
    remainingDuration,
  ]);

  // This useEffect does some actions only when timer is running (state -> STARTED)
  useEffect(() => {
    if (state === "STARTED") {
      if (
        remainingDuration == 300 &&
        !showTestAboutToCompleteDialog &&
        isWorksheetSubmissionModal
      ) {
        setShowTestAboutToCompleteDialog(true);
      }

      if (remainingDuration <= 300 && !showDestructiveTimer) {
        setShowDestructiveTimer(true);
      }
    }

    if (
      !isTestEnded &&
      !isTestStarted &&
      state === "ENDED" &&
      remainingDuration <= 0 &&
      _.isFunction(onTimesUp)
    ) {
      // * Call times up callback when test not started and time is up
      onTimesUp();
    }

    if (
      isReopened &&
      state === "NOT_STARTED" &&
      remainingDuration > 0 &&
      _.isFunction(onTimesUp)
    ) {
      // * Reset times up callback when test is reopened and not started
      onTimesUp(false);
    }
  }, [
    state,
    remainingDuration,
    showTestAboutToCompleteDialog,
    showDestructiveTimer,
    isWorksheetSubmissionModal,
    isReopened,
    isTestEnded,
    isTestStarted,
  ]);

  // This useEffect runs when test is ended by student on click submit button
  // No need to call registerTimeTrackingEvent here as it is already called in submit button handler
  useEffect(() => {
    if (state === "STARTED" && isTestEnded) {
      setTimer({ state: "ENDED", remainingDuration: 0 });
      setShowDestructiveTimer(false);
      setShowTestAboutToCompleteDialog(false);
      clearInterval(intervalRef?.current);
    }
  }, [isTestEnded, state]);

  // This useEffect does some actions only when timer is ended (state -> ENDED)
  useEffect(() => {
    // Don't auto submit if worksheet is paused or (reopened and not started and not ended)
    if (isWorksheetPaused || (isReopened && !isTestStarted && !isTestEnded))
      return;

    if (state === "ENDED" && !isTestEnded && isTestStarted) {
      const afterTimerExpires = async () => {
        await onSubmitTimedWorksheet({ source: "AUTO_SUBMIT_FROM_TIMER" });

        if (isSafeBrowserEnabled && isInSafeExamBrowser) {
          handleQuitSebNavigate();
        }

        if (isWorksheetSubmissionModal) {
          toggleWorksheetModal(false);
        }
      };
      setWorksheetWebViewRecentlySubmitted(true);
      if (!isQuestionTheaterModeEnable) {
        setShowDestructiveTimer(false);
        setShowTestAboutToCompleteDialog(false);
        clearInterval(intervalRef?.current);
        afterTimerExpires();
      } else {
        updateEnableAutoSubmit(true);
      }
    }
  }, [
    state,
    isTestEnded,
    isWorksheetSubmissionModal,
    onSubmitTimedWorksheet,
    toggleWorksheetModal,
    isQuestionTheaterModeEnable,
    isReopened,
    isTestStarted,
    isWorksheetPaused,
  ]);

  const getDurationTextToDisplay = () => {
    const endedClasses = classNames("text-label-s", classes.textSecondary);

    switch (state) {
      case "ENDED":
      case "TIMES_UP":
        return isStudentAssignmentRevamp ? (
          <div className={endedClasses}>{t("common:ended")}</div>
        ) : (
          t("common:ended")
        );
      default: {
        const formattedDuration = getFormattedDuration({
          duration: remainingDuration,
          unit: "seconds",
          durationFormat: "HH:mm:ss",
        });

        const showRemainingSuffix =
          isStudentAssignmentRevamp &&
          (!isWorksheetSubmissionModal || !isStudentUIRevamp);
        return (
          formattedDuration +
          (showRemainingSuffix ? " " + t("common:remaining") : "")
        );
      }
    }
  };

  const renderTimerContent = () => {
    // This determines if we should use a new version of the timer UI
    const showRevampedTimer =
      isStudentAssignmentRevamp && !isWorksheetSubmissionModal;

    const modifiedStyle = {
      backgroundColor: "unset",
    };

    const timerIconVariant = showDestructiveTimer ? "critical" : "warning";

    const containerClasses = classNames({
      [classes.container]: true,
      [classes.destructiveContainer]: showDestructiveTimer,
    });
    const durationClasses = classNames({
      [classes.duration]: true,
      [classes.destructiveDuration]: showDestructiveTimer,
    });
    const revampDurationClasses = classNames("text-label-s", {
      [classes.revampDuration]: true,
      [classes.destructiveDuration]: showDestructiveTimer,
    });
    const timerPillClasses = classNames({
      [classes.timerPill]: true,
      [classes.timerPillRevamp]: true,
      [classes.timePillMobile]: isMobileDevice,
    });

    const onCloseDialogModal = () => {
      setShowTestAboutToCompleteDialog(false);
    };

    if (isStudentUIRevamp && isWorksheetSubmissionModal) {
      return (
        <>
          <div className={timerPillClasses}>
            {!isMobileDevice && (
              <ClockTimerOutlined
                variant={timerIconVariant}
                size={"xxx-small"}
              />
            )}
            <div className={durationClasses}>{getDurationTextToDisplay()}</div>
          </div>
          {showTestAboutToCompleteDialog && (
            <TestAboutToCompleteDialog
              worksheetTitle={worksheetTitle}
              onClose={onCloseDialogModal}
            />
          )}
        </>
      );
    }

    if (showRevampedTimer) {
      return (
        <>
          <BulletDotFilled variant="subtle" size="xxx-small" />
          <div className={revampDurationClasses}>
            {getDurationTextToDisplay()}
          </div>
          {showTestAboutToCompleteDialog && (
            <TestAboutToCompleteDialog
              worksheetTitle={worksheetTitle}
              onClose={onCloseDialogModal}
            />
          )}
        </>
      );
    }

    return (
      <div
        className={containerClasses}
        style={!showBackground ? modifiedStyle : null}
      >
        <ClockTimerOutlined variant={timerIconVariant} size={"xx-small"} />
        <div className={durationClasses}>{getDurationTextToDisplay()}</div>
        {showTestAboutToCompleteDialog && (
          <TestAboutToCompleteDialog
            worksheetTitle={worksheetTitle}
            onClose={onCloseDialogModal}
          />
        )}
      </div>
    );
  };

  return renderTimerContent();
});

WorksheetTimerV2.displayName = "WorksheetTimerV2";

const WorksheetTimer = React.memo(props => {
  const {
    startEvent,
    isTestEnded,
    isTestStarted,
    timedTestDuration,
    isWorksheetSubmissionModal = false,
    worksheetTitle,
    toggleWorksheetModal,
    onSubmitTimedWorksheet,
    setWorksheetWebViewRecentlySubmitted,
    showBackground,
    isSafeBrowserEnabled,
    isInSafeExamBrowser,
    isQuestionTheaterModeEnable,
    updateEnableAutoSubmit,
    closeSubmissionDate,
  } = props;

  const { t } = useI18n();
  const intervalRef = useRef();

  const isStudentUIRevamp = isStudentEndMode();

  const getStartedTimerState = () => {
    const startTime = _.get(startEvent, "eventTime", null);
    let endTime = getIncrementedDate({
      date: startTime,
      key: "minutes",
      incrementBy: _.toNumber(timedTestDuration),
    });
    const currentTime = getTodaysDate();

    if (closeSubmissionDate) {
      if (!startTime) {
        endTime = closeSubmissionDate;
      } else {
        endTime = moment.min(endTime, moment(closeSubmissionDate));
      }
    }

    const isCurrentTimeSameOrAfterEndTime = checkIsDateSameOrAfter({
      date1: currentTime,
      date2: endTime,
      key: "seconds",
    });

    if (isCurrentTimeSameOrAfterEndTime) {
      return {
        state: "ENDED",
        remainingDuration: 0,
      };
    }

    return {
      state: "STARTED",
      remainingDuration: getDateTimeDifference({
        date1: endTime,
        date2: currentTime,
        differenceBy: "seconds",
      }),
    };
  };

  const getInitialTimerState = () => {
    if (isTestEnded) {
      return { state: "ENDED", remainingDuration: 0 };
    }

    if (isTestStarted) {
      return getStartedTimerState();
    }

    // Test not started yet
    let remainingDuration = _.toNumber(timedTestDuration) * 60;

    if (closeSubmissionDate) {
      const closingDuration = getDateTimeDifference({
        date1: closeSubmissionDate,
        date2: getTodaysDate(),
        differenceBy: "seconds",
      });

      remainingDuration = Math.max(
        Math.min(remainingDuration, closingDuration),
        0
      );

      if (remainingDuration <= 0) {
        if (_.isFunction(props.onTimesUp)) props.onTimesUp();
        return { state: "TIMES_UP", remainingDuration: 0 };
      }
    }

    return {
      state: "NOT_STARTED",
      remainingDuration: remainingDuration,
    };
  };

  const [timer, setTimer] = useState(() => getInitialTimerState());
  const [
    showTestAboutToCompleteDialog,
    setShowTestAboutToCompleteDialog,
  ] = useState(false);
  const [showDestructiveTimer, setShowDestructiveTimer] = useState(false);

  useEffect(() => {
    // Update initiat timer for closes date
    // For reopened tests, we do not need to setup this interval since it is not dependent on closeSubmissionDate
    if (isTestEnded || isTestStarted || !closeSubmissionDate) return;

    const intervalId = setInterval(() => {
      const initTime = getInitialTimerState();
      setTimer(initTime);
      if (initTime?.remainingDuration <= 0) {
        setShowDestructiveTimer(true);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isTestStarted, isTestEnded, closeSubmissionDate]);

  useEffect(() => {
    setTimer(getInitialTimerState());
  }, [timedTestDuration, isTestStarted, isTestEnded]);

  // This useEffect sets up interval to update timer every second
  useEffect(() => {
    if (isTestStarted && !isTestEnded) {
      intervalRef.current = setInterval(() => {
        setTimer(getStartedTimerState());
      }, 1000);
    }

    return () => {
      clearInterval(intervalRef?.current);
    };
  }, [isTestStarted, isTestEnded, timedTestDuration]);

  // This useEffect does some actions only when timer is running (state -> STARTED)
  useEffect(() => {
    const { state, remainingDuration } = timer;
    if (state === "STARTED") {
      if (
        remainingDuration == 300 &&
        !showTestAboutToCompleteDialog &&
        isWorksheetSubmissionModal
      ) {
        setShowTestAboutToCompleteDialog(true);
      }

      if (remainingDuration <= 300 && !showDestructiveTimer) {
        setShowDestructiveTimer(true);
      }
    }
  }, [
    timer,
    showTestAboutToCompleteDialog,
    showDestructiveTimer,
    isWorksheetSubmissionModal,
  ]);

  // This useEffect does some actions only when timer is ended (state -> ENDED)
  useEffect(() => {
    if (timer.state === "ENDED" && !isTestEnded) {
      const afterTimerExpires = async () => {
        await onSubmitTimedWorksheet({ source: "AUTO_SUBMIT_FROM_TIMER" });

        if (isSafeBrowserEnabled && isInSafeExamBrowser) {
          handleQuitSebNavigate();
        } else {
          null;
        }

        if (isWorksheetSubmissionModal) {
          toggleWorksheetModal(false);
        }
      };
      setWorksheetWebViewRecentlySubmitted(true);
      if (!isQuestionTheaterModeEnable) {
        setShowDestructiveTimer(false);
        setShowTestAboutToCompleteDialog(false);
        clearInterval(intervalRef?.current);
        afterTimerExpires();
      } else {
        updateEnableAutoSubmit(true);
      }
    }
  }, [
    timer.state,
    isTestEnded,
    isWorksheetSubmissionModal,
    onSubmitTimedWorksheet,
    toggleWorksheetModal,
    isQuestionTheaterModeEnable,
    isTestStarted,
  ]);

  // This useEffect runs when test is ended by student on click submit button
  // No need to call registerTimeTrackingEvent here as it is already called in submit button handler
  useEffect(() => {
    if (timer.state === "STARTED" && isTestEnded) {
      setTimer({ state: "ENDED", remainingDuration: 0 });
      setShowDestructiveTimer(false);
      setShowTestAboutToCompleteDialog(false);
      clearInterval(intervalRef?.current);
    }
  }, [isTestEnded, timer.state]);

  const isStudentAssignmentRevamp = getIsStudentAssignmentRevampEnabled();

  const getDurationTextToDisplay = () => {
    const { state, remainingDuration } = timer;

    const endedClasses = classNames("text-label-s", classes.textSecondary);

    switch (state) {
      case "ENDED":
      case "TIMES_UP":
        return isStudentAssignmentRevamp ? (
          <div className={endedClasses}>{t("common:ended")}</div>
        ) : (
          t("common:ended")
        );
      default: {
        const formattedDuration = getFormattedDuration({
          duration: remainingDuration,
          unit: "seconds",
          durationFormat: "HH:mm:ss",
        });

        const showRemainingSuffix =
          isStudentAssignmentRevamp &&
          (!isWorksheetSubmissionModal || !isStudentUIRevamp);
        return (
          formattedDuration +
          (showRemainingSuffix ? " " + t("common:remaining") : "")
        );
      }
    }
  };

  const onCloseDialogModal = () => {
    setShowTestAboutToCompleteDialog(false);
  };

  const containerClasses = classNames({
    [classes.container]: true,
    [classes.destructiveContainer]: showDestructiveTimer,
  });

  const modifiedStyle = {
    backgroundColor: "unset",
  };

  const durationClasses = classNames({
    [classes.duration]: true,
    [classes.destructiveDuration]: showDestructiveTimer,
  });
  const revampDurationClasses = classNames("text-label-s", {
    [classes.revampDuration]: true,
    [classes.destructiveDuration]: showDestructiveTimer,
  });

  const timerIconVariant = showDestructiveTimer ? "critical" : "warning";

  // This determines if we should use a new version of the timer UI
  const showRevampedTimer =
    isStudentAssignmentRevamp && !isWorksheetSubmissionModal;

  const renderTimerContent = () => {
    const timerPillClasses = classNames({
      [classes.timerPill]: true,
      [classes.timepillTab]: isWorksheetWebView(),
    });
    if (isStudentUIRevamp && isWorksheetSubmissionModal) {
      return (
        <>
          <div className={timerPillClasses}>
            <ClockTimerOutlined variant={timerIconVariant} size={"xx-small"} />
            <div className={durationClasses}>{getDurationTextToDisplay()}</div>
          </div>
          {showTestAboutToCompleteDialog && (
            <TestAboutToCompleteDialog
              worksheetTitle={worksheetTitle}
              onClose={onCloseDialogModal}
            />
          )}
        </>
      );
    }

    if (showRevampedTimer) {
      return (
        <>
          <BulletDotFilled variant="subtle" size="xxx-small" />
          <div className={revampDurationClasses}>
            {getDurationTextToDisplay()}
          </div>
          {showTestAboutToCompleteDialog && (
            <TestAboutToCompleteDialog
              worksheetTitle={worksheetTitle}
              onClose={onCloseDialogModal}
            />
          )}
        </>
      );
    }

    return (
      <div
        className={containerClasses}
        style={!showBackground ? modifiedStyle : null}
      >
        <ClockTimerOutlined variant={timerIconVariant} size={"xx-small"} />
        <div className={durationClasses}>{getDurationTextToDisplay()}</div>
        {showTestAboutToCompleteDialog && (
          <TestAboutToCompleteDialog
            worksheetTitle={worksheetTitle}
            onClose={onCloseDialogModal}
          />
        )}
      </div>
    );
  };

  return renderTimerContent();
});

const mapStateToProps = state => {
  const isQuestionTheaterModeEnable = _.get(
    state,
    "worksheet.isQuestionTheaterModeEnable",
    false
  );
  return { isQuestionTheaterModeEnable };
};

const mapActionCreators = { updateEnableAutoSubmit };

WorksheetTimer.displayName = "WorksheetTimer";

const WorksheetTimerWrapper = props => {
  const isWorksheetMonitoringEnabled = getIsWorksheetMonitoringEnabled();

  const WorksheetTimerComponent = isWorksheetMonitoringEnabled
    ? WorksheetTimerV2
    : WorksheetTimer;

  return <WorksheetTimerComponent {...props} />;
};

WorksheetTimerWrapper.defaultProps = {
  showBackground: true,
  isSafeBrowserEnabled: false,
  isInSafeExamBrowser: false,
  isQuestionTheaterModeEnable: false,
  setWorksheetWebViewRecentlySubmitted: _.noop,
  submissionEndTime: null,
  pauseEventTime: null,
  isWorksheetPaused: false,
  totalPausedDuration: 0,
  isReopened: false,
  totalReopenDuration: 0,
  startTimeAfterReopen: null,
  onTimesUp: _.noop,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(WorksheetTimerWrapper);
